import { type NavigationFailure, useRoute, useRouter } from "vue-router";

import type {
  AnstalldaFilterKategoriType,
  PensionsplanType,
} from "@/composables/KundserviceAppApiClient";

export interface INavigation {
  router: ReturnType<typeof useRouter>;
  currentRoute: ReturnType<typeof useRoute>;
  search: (query: string) => void;
  notFound: (notFound?: string) => void;
  clearNotFound: () => void;
  openAnstalld: (id: string, arendeId?: string) => void;
  openArbetsgivare: (
    organisationsnummer: string,
    arendeId?: string | null,
    arbetsgivarrepresentantId?: string | null,
  ) => void;
  clearPuzzelParams: () => void;
  replacePersonnummerWithId: (
    id: string,
  ) => Promise<void | NavigationFailure | undefined>;
  replacePersonnummerWithArbetsgivarrepresentantId: (id: string) => void;
  clearArbetsgivareAnstalldaQuery: () => void;
  filterArbetsgivareAnstallda: (
    currentPage: number,
    currentPageSize: string,
    kategori?: AnstalldaFilterKategoriType,
    pensionsplan?: PensionsplanType,
  ) => void;
  openRelease: (fullName: string) => void;
  openArendeView: (arendeId: number | string) => void;
  closeArendeView: () => void;
  clearVerified: () => Promise<void | NavigationFailure | undefined>;
}

export function useNavigation(): INavigation {
  const router = useRouter();
  const currentRoute = useRoute();

  const search = (query: string) => {
    router.push({
      name: "home",
      query: {
        q: query,
        source: undefined,
        kundgrupp: currentRoute.query.kundgrupp,
        mediakanal: currentRoute.query.mediakanal,
        verified: currentRoute.query.ver,
        externalpuzzelid: currentRoute.query.externalpuzzelid,
      },
    });
  };

  const notFound = (notFound?: string) => {
    router.push({
      name: "home",
      query: {
        ...currentRoute.query,
        notfound: notFound,
        source: undefined,
      },
    });
  };

  const clearNotFound = () => {
    router.replace({
      query: {
        ...currentRoute.query,
        notfound: undefined,
      },
    });
  };

  const openAnstalld = (id: string, arendeId?: string) => {
    router.push({
      name: "anstalld",
      query: {
        id,
        source: undefined,
        q: undefined,
        notfound: undefined,
        kundgrupp: currentRoute.query.kundgrupp,
        mediakanal: currentRoute.query.mediakanal,
        verified: currentRoute.query.ver,
        manuallyVerified: currentRoute.query.manver,
        externalpuzzelid: currentRoute.query.externalpuzzelid,
        ticket_id: arendeId,
      },
    });
  };

  const openArbetsgivare = (
    organisationsnummer: string,
    arendeId?: string | null,
    arbetsgivarrepresentantId?: string | null,
  ) => {
    router.push({
      name: "arbetsgivare",
      query: {
        organisationsnummer,
        arbetsgivarrepresentantId: arbetsgivarrepresentantId ?? undefined,
        source: undefined,
        q: undefined,
        notfound: undefined,
        kundgrupp: currentRoute.query.kundgrupp,
        mediakanal: currentRoute.query.mediakanal,
        verified: currentRoute.query.ver,
        externalpuzzelid: currentRoute.query.externalpuzzelid,
        ticket_id: arendeId,
      },
    });
  };

  const clearPuzzelParams = () => {
    router.replace({
      query: {
        ...currentRoute.query,
        source: undefined,
        kundgrupp: undefined,
        mediakanal: undefined,
        verified: undefined,
        externalpuzzelid: undefined,
      },
    });
  };

  const replacePersonnummerWithId = (id: string) =>
    router.replace({
      ...router.currentRoute,
      query: {
        id: id,
        ...currentRoute.query,
        personnummer: undefined,
      },
    });

  const replacePersonnummerWithArbetsgivarrepresentantId = (id: string) => {
    router.replace({
      ...router.currentRoute,
      query: {
        arbetsgivarrepresentantId: id,
        ...currentRoute.query,
        personnummer: undefined,
      },
    });
  };

  const clearArbetsgivareAnstalldaQuery = () => {
    router.replace({
      query: {
        ...currentRoute.query,
        kategori: undefined,
        pensionsplan: undefined,
        page: undefined,
        pageSize: undefined,
      },
    });
  };

  const filterArbetsgivareAnstallda = (
    currentPage: number,
    currentPageSize: string,
    kategori?: AnstalldaFilterKategoriType,
    pensionsplan?: PensionsplanType,
  ) => {
    router.replace({
      ...router.currentRoute,
      query: {
        ...currentRoute.query,
        kategori: kategori,
        pensionsplan: pensionsplan,
        page: currentPage,
        pageSize: currentPageSize,
      },
    });
  };

  const openRelease = (fullName: string) => {
    router.push({
      name: "release",
      params: {
        fullName,
      },
    });
  };

  const openArendeView = (arendeId: number | string) => {
    router.replace({
      query: {
        ...currentRoute.query,
        ticket_id: arendeId,
      },
    });
  };

  const closeArendeView = () => {
    router.replace({
      query: {
        ...currentRoute.query,
        ticket_id: undefined,
      },
    });
  };

  const clearVerified = () =>
    router.replace({
      query: {
        ...currentRoute.query,
        verified: undefined,
        manuallyVerified: undefined,
      },
    });

  return {
    router,
    currentRoute,
    search,
    notFound,
    clearNotFound,
    openAnstalld,
    openArbetsgivare,
    clearPuzzelParams,
    replacePersonnummerWithId,
    replacePersonnummerWithArbetsgivarrepresentantId,
    clearArbetsgivareAnstalldaQuery,
    filterArbetsgivareAnstallda,
    openRelease,
    openArendeView: openArendeView,
    closeArendeView: closeArendeView,
    clearVerified,
  };
}
